import { Button, Card } from "@fanduel/og-fe-library";
import cx from "classnames";
import Link from "next/link";
import NumberFireIcon from "@/components/svg/NumberFireIcon";
import TrackMasterIcon from "@/components/svg/TrackMasterIcon";
import { RACING_PROJECTIONS_CTA_CLICKED } from "@/constants/amplitude";
import { Hr_Race } from "@/graphql/gql/graphql";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import { getFDRLink } from "@/utils/horseRacing";
import { filterEmpty } from "@/utils/index";
import useHorseRacingRouting from "../hooks/useHorseRacingRouting";
import RaceTime from "../raceTime";
import ConsensusInfoTableRow from "./ConsensusInfoTableRow";
import styles from "./raceInfo.module.scss";

type RaceWithPicks = Hr_Race & { pickSources: ("timeform" | "handicapping")[] };

interface ConsensusInfoProps {
  race: Hr_Race | RaceWithPicks;
}

const ConsensusInfo = ({ race }: ConsensusInfoProps) => {
  const interests = filterEmpty(race.bettingInterests ?? []);
  const { logAmplitudeEvent } = useAmplitude();
  const { buildHrefObject, buildHrefString } = useHorseRacingRouting();

  const projectionsHref = buildHrefObject(
    race.track?.code ?? "",
    race.track?.name ?? "",
    race.number,
    2
  );

  const projectionsUrl = buildHrefString(
    race.track?.code ?? "",
    race.track?.name ?? "",
    race.number,
    null
  );

  return (
    <Card elevation="mid" variant="elevation" rounded="sm">
      <div className={styles.header}>
        {race.postTime ? (
          <div>
            <RaceTime postTime={race.postTime} />
          </div>
        ) : (
          ""
        )}
        <div>
          <span className={styles.trackName}>{race.track?.name}</span>
          <span>•</span>
          <span>{`Race ${race.number}`}</span>
        </div>
      </div>
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead className={styles.tableHeader}>
            <tr>
              <th className={styles.tableCol}>#/Silk</th>
              <th
                colSpan={5}
                className={cx(styles.tableCol, styles.middle, styles.consensus)}
              >
                Horse
              </th>
              <th
                aria-label="numberFire pick"
                className={cx(styles.tableCol, styles.pickCol)}
              >
                <NumberFireIcon />
              </th>
              <th
                aria-label="trackMaster pick"
                className={cx(styles.tableCol, styles.pickCol)}
              >
                <TrackMasterIcon />
              </th>
              <th className={styles.tableCol}>Odds</th>
            </tr>
          </thead>
          <tbody>
            {interests.map((bets) => (
              <ConsensusInfoTableRow
                saddleColor={bets.saddleColor ?? undefined}
                numberColor={bets.numberColor ?? undefined}
                runners={bets?.runners}
                numerator={bets.currentOdds?.numerator}
                denominator={bets.currentOdds?.denominator}
                key={
                  bets?.runners?.[0]?.entityRunnerId ??
                  bets?.runners?.[0]?.horseName
                }
                fdrLink={getFDRLink(
                  race.track?.code ?? "",
                  race.track?.name ?? "",
                  race.number ?? undefined
                )}
                pickSources={
                  "pickSources" in race ? race?.pickSources : undefined
                }
                raceNumber={race.number}
                trackName={race.track?.name}
                postTime={race.postTime}
              />
            ))}
          </tbody>
        </table>
      </div>
      <div className={styles.footer}>
        <Link href={projectionsHref}>
          <Button
            rightIconName="arrows.right"
            className={styles.footerButton}
            buttonStyle="Outlined"
            buttonColor="Blue"
            buttonText="See Race Projections"
            onClick={() => {
              logAmplitudeEvent(RACING_PROJECTIONS_CTA_CLICKED, {
                Module: "consensus_picks",
                "Link Text": "see_race_projections",
                "Link URL": projectionsUrl,
              });
            }}
          />
        </Link>
      </div>
    </Card>
  );
};

export default ConsensusInfo;
